<script setup>
// BaseSettingsHeader — header padrão de TODAS as páginas de /settings/*.
// Refatorado pra usar o mesmo visual do StudioPageHeader (ClickUp-style):
// background full-width, ícone em tile accent-aware, título + subtitle,
// border-b e botão de back/help posicionados de forma consistente.
//
// API mantida (não quebra os 19 callers existentes):
//   title, description, iconName, linkText, featureName, backButtonLabel
//   slots: actions, description
import { computed } from 'vue';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  // fluent-icon name (compat com callers atuais). Renderizado dentro do
  // tile accent-aware.
  iconName: { type: String, default: '' },
  linkText: { type: String, default: '' },
  featureName: { type: String, default: '' },
  backButtonLabel: { type: String, default: '' },
  // Tom da paleta Radix-aware (CLAUDE.md). 'blue' mantém o look azul
  // que o BaseSettingsHeader antigo já usava por default.
  accent: {
    type: String,
    default: 'blue',
    validator: v =>
      [
        'slate',
        'iris',
        'teal',
        'blue',
        'ruby',
        'amber',
        'orange',
        'cyan',
        'green',
        'gray',
      ].includes(v),
  },
});

const helpURL = getHelpUrlForFeature(props.featureName);

const openInNewTab = url => {
  if (!url) return;
  window.open(url, '_blank', 'noopener noreferrer');
};

// Bg + foreground por accent — mesma convenção Radix do StudioPageHeader
// (3 = bg sutil, 11 = texto contrastado).
const accentClasses = computed(
  () =>
    ({
      slate: 'bg-n-slate-3 text-n-slate-11',
      iris: 'bg-n-iris-3 text-n-iris-11',
      teal: 'bg-n-teal-3 text-n-teal-11',
      blue: 'bg-n-blue-3 text-n-blue-11',
      ruby: 'bg-n-ruby-3 text-n-ruby-11',
      amber: 'bg-n-amber-3 text-n-amber-11',
      orange: 'bg-n-orange-3 text-n-orange-11',
      cyan: 'bg-n-cyan-3 text-n-cyan-11',
      green: 'bg-n-green-3 text-n-green-11',
      gray: 'bg-n-gray-3 text-n-gray-11',
    })[props.accent]
);
</script>

<template>
  <header
    class="shrink-0 bg-n-background px-6 pt-8 pb-7 border-b border-solid border-n-weak"
  >
    <BackButton
      v-if="backButtonLabel"
      compact
      :button-label="backButtonLabel"
      class="mb-3"
    />

    <div class="flex items-start justify-between gap-4">
      <div class="flex items-start gap-4 min-w-0">
        <div
          v-if="iconName"
          class="shrink-0 flex items-center justify-center size-14 rounded-lg"
          :class="accentClasses"
        >
          <fluent-icon
            size="28"
            :icon="iconName"
            type="outline"
            class="flex-shrink-0"
          />
        </div>
        <div class="min-w-0 flex-1">
          <h1 class="text-xl font-semibold text-n-slate-12 leading-tight">
            {{ title }}
          </h1>
          <div class="mt-1 text-sm text-n-slate-11 leading-relaxed">
            <slot name="description">
              <p
                v-dompurify-html="description"
                class="mb-0 line-clamp-5 sm:line-clamp-none max-w-3xl"
              />
            </slot>
            <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
              <a
                v-if="helpURL && linkText"
                :href="helpURL"
                target="_blank"
                rel="noopener noreferrer"
                class="items-center hidden gap-1 mt-2 text-sm font-medium sm:inline-flex w-fit text-n-blue-text hover:underline"
              >
                {{ linkText }}
                <Icon
                  icon="i-lucide-chevron-right"
                  class="flex-shrink-0 text-n-blue-text size-4"
                />
              </a>
            </CustomBrandPolicyWrapper>
          </div>
        </div>
      </div>

      <!-- Actions desktop -->
      <div
        v-if="$slots.actions"
        class="shrink-0 hidden sm:flex items-center gap-2"
      >
        <slot name="actions" />
      </div>
    </div>

    <!-- Actions mobile (sob título quando viewport estreito) -->
    <div
      v-if="$slots.actions || (helpURL && linkText)"
      class="flex flex-wrap items-start gap-3 mt-4 sm:hidden"
    >
      <slot name="actions" />
      <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
        <Button
          v-if="helpURL && linkText"
          blue
          link
          icon="i-lucide-chevron-right"
          trailing-icon
          :label="linkText"
          @click="openInNewTab(helpURL)"
        />
      </CustomBrandPolicyWrapper>
    </div>
  </header>
</template>
